import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  AddCardParams,
  Billing,
  BillingResponse,
  Card,
  CardResponse,
  DiscountDetailsParams,
  DiscountDetailsResponse,
  Plan,
  PlanResponse,
  SubscribeParams,
  SubscriptionResponse,
  SubscriptionUsage,
  SubscriptionUsageResponse,
  SwitchSubscriptionParams,
} from './subscription.model';
import { GenericApiResponse } from '../general-store/general.model';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private readonly apiUrl = environment.apiUrl;

  private readonly http = inject(HttpClient);

  public getPlans(): Observable<Plan[]> {
    const url = `${this.apiUrl}/api/plans`;
    return this.http.get<PlanResponse>(url).pipe(
      map((response) => response.payload),
      catchError((error) => {
        return throwError(() => new Error('Error fetching plans: ' + error.message));
      }),
    );
  }

  public getSubscriptionUsage(): Observable<SubscriptionUsage> {
    return this.http
      .get<SubscriptionUsageResponse>(`${this.apiUrl}/api/qr-product-data/dashboard-stats`)
      .pipe(
        map((response) => response.payload),
        catchError((error) => {
          return throwError(() => new Error('Error fetching subscription usage: ' + error.message));
        }),
      );
  }

  public getCards(): Observable<Card[]> {
    return this.http.get<CardResponse>(`${this.apiUrl}/api/cards`).pipe(
      map((response) => response.payload.data),
      catchError((error) => {
        return throwError(() => new Error('Error fetching cards: ' + error.message));
      }),
    );
  }

  public getBillings(): Observable<Billing> {
    return this.http.get<BillingResponse>(`${this.apiUrl}/api/billing/all`).pipe(
      map((response) => response.payload),
      catchError((error) => {
        return throwError(() => new Error('Error fetching billings: ' + error.message));
      }),
    );
  }

  public addCard(payload: AddCardParams): Observable<GenericApiResponse> {
    return this.http.post<GenericApiResponse>(`${this.apiUrl}/api/cards`, payload).pipe(
      map((response) => response),
      catchError((error) => {
        return throwError(() => new Error('Error adding card: ' + error.message));
      }),
    );
  }

  public discountDetails(payload: DiscountDetailsParams): Observable<DiscountDetailsResponse> {
    return this.http
      .post<DiscountDetailsResponse>(`${this.apiUrl}/api/subscriptions/discount-details`, payload)
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error adding card: ' + error.message));
        }),
      );
  }

  public setCardAsDefault(cardId: string): Observable<GenericApiResponse> {
    return this.http
      .patch<GenericApiResponse>(`${this.apiUrl}/api/cards/${cardId}/default`, {})
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error setting card as default: ' + error.message));
        }),
      );
  }

  public deleteCard(cardId: string): Observable<GenericApiResponse> {
    return this.http.delete<GenericApiResponse>(`${this.apiUrl}/api/cards/${cardId}`).pipe(
      map((response) => response),
      catchError((error) => {
        return throwError(() => new Error('Error deleting card: ' + error.message));
      }),
    );
  }

  public subscribe(payload: SubscribeParams): Observable<SubscriptionResponse> {
    return this.http.post<SubscriptionResponse>(`${this.apiUrl}/api/subscriptions`, payload).pipe(
      map((response) => response),
      catchError((error) => {
        return throwError(() => new Error('Error subscribing: ' + error.message));
      }),
    );
  }

  public switchSubscription(
    id: number,
    payload: SwitchSubscriptionParams,
  ): Observable<GenericApiResponse> {
    return this.http
      .post<GenericApiResponse>(`${this.apiUrl}/api/subscriptions/${id}/switch`, payload)
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error switching subscription: ' + error.message));
        }),
      );
  }

  public cancelSubscription(id: string): Observable<GenericApiResponse> {
    return this.http.delete<GenericApiResponse>(`${this.apiUrl}/api/subscriptions/${id}`).pipe(
      map((response) => response),
      catchError((error) => {
        return throwError(() => new Error('Error canceling subscription: ' + error.message));
      }),
    );
  }
}
